"use client";

import { API, BLOB_URL } from "@/Configuration";
import { Story } from "@/interfaces/story";
import { LanguageISOCode, Translations } from "@/Languages";
import {
  ChevronLeft,
  ChevronRight,
  CirclePause,
  CirclePlay,
  LoaderCircle,
  X,
} from "lucide-react";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

export default function Player({
  speed,
  autoplay,
  LANG,
}: Readonly<{
  speed: number;
  autoplay: boolean;
  LANG: Translations;
}>) {
  const playerRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const [loading, setLoading] = useState<boolean>(false);

  const [state, setState] = useState<{
    story?: Story;
    playlist?: string[];
    language: LanguageISOCode;
    simple: boolean;
  }>({ story: undefined, language: "de", simple: false });

  const [playing, setPlaying] = useState<boolean>(false);
  const [duration, setDuration] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(0);

  const [playbackRate, setPlaybackRate] = useState<number>(speed);

  const languageElement =
    state.story?.storyLanguages?.find((e) => e.language === state.language) ??
    state.story;

  useEffect(() => {
    const el = document.getElementById("playerHeight");
    el?.setAttribute(
      "style",
      `height: ${playerRef.current?.clientHeight ?? 0}px`,
    );
  }, [state]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = playbackRate;
  }, [playbackRate]);

  const playBlob = (blob: Blob) => {
    const audio = new Audio();
    audioRef.current = audio;
    audio.src = URL.createObjectURL(blob);

    audio.onloadedmetadata = () => {
      const audioDuration = audio.duration;
      if (typeof audioDuration === "number" && !isNaN(audioDuration)) {
        setDuration(audioDuration);
      }
      audio.playbackRate = playbackRate;
      audio.play();
      setPlaying(true);
      setLoading(false);
    };

    audio.ontimeupdate = () => {
      if (audio.duration === audio.currentTime) {
        audio.currentTime = 0;
        audio.pause();
        setPlaying(false);
        if (autoplay) handlePlaylistNext(+1);
      }
      setCurrentTime(audio.currentTime);
    };
  };

  const playAudio = (storyId: string, lang: string, simple: boolean) => {
    console.log(`PLAY: id:${storyId} lang:${lang} simple:${simple}`);
    fetch(
      `${BLOB_URL}/audiofiles/${storyId}/${lang}${simple ? "-simple" : ""}.mp3`,
    ).then((res) => {
      if (res.ok) {
        res.blob().then((blob) => playBlob(blob));
      } else if (simple) {
        playAudio(storyId, lang, false);
      } else if (lang !== "de") {
        playAudio(storyId, "de", false);
      }
    });
  };

  useEffect(() => {
    setLoading(true);
    setDuration(0);
    setCurrentTime(0);
    setPlaying(false);

    if (audioRef.current) {
      audioRef.current.onloadedmetadata = null;
      audioRef.current.ontimeupdate = null;
      audioRef.current.srcObject = null;
      audioRef.current.src = "";
    }

    if (!state.story) return;

    playAudio(state.story.id, languageElement?.language ?? "de", state.simple);
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const playButtonEvent = (event: CustomEvent) => {
      let playlistValue = state.playlist;

      const isPlaylist = typeof event.detail.storyIdOrPlaylist !== "string";

      if (isPlaylist) {
        playlistValue = event.detail.storyIdOrPlaylist;
      }

      if (
        !isPlaylist &&
        state.playlist &&
        !state.playlist.includes(event.detail.storyIdOrPlaylist)
      ) {
        playlistValue = undefined;
      }

      const storyId = isPlaylist
        ? event.detail.storyIdOrPlaylist[0]
        : event.detail.storyIdOrPlaylist;

      fetch(`${API}/story?storyId=${storyId}`)
        .then((res) => res.ok && res.json())
        .then((story) =>
          setState({
            story: story,
            language: event.detail.language,
            simple: event.detail.simple,
            playlist: playlistValue,
          }),
        );
    };

    const changeOptionsEvent = (event: CustomEvent) => {
      if (!state.story) return;
      if (event.detail.lang !== state.language) {
        setState((prev) => ({ ...prev, language: event.detail.lang }));
      }
      if (event.detail.simple !== state.simple) {
        setState((prev) => ({ ...prev, simple: event.detail.simple }));
      }
    };

    window.addEventListener("play", playButtonEvent as EventListener);
    window.addEventListener(
      "changeOptions",
      changeOptionsEvent as EventListener,
    );
    return () => {
      window.removeEventListener("play", playButtonEvent as EventListener);
      window.removeEventListener(
        "changeOptions",
        changeOptionsEvent as EventListener,
      );
    };
  }, [state]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const handlePlayPause = () => {
    if (playing) {
      audioRef.current?.pause();
      setPlaying(false);
    } else {
      audioRef.current?.play();
      setPlaying(true);
    }
  };

  const handlePlaylistNext = (n: number) => {
    if (!state.playlist) return;
    if (!state.story) return;
    const id = state.playlist[state.playlist.indexOf(state.story.id) + n];
    if (!id) return;
    fetch(`${API}/story?storyId=${id}`)
      .then((res) => res.ok && res.json())
      .then((story) => setState((prev) => ({ ...prev, story })));
  };

  return (
    state.story && (
      <div className="bg-player text-text" ref={playerRef}>
        <div className="container mx-auto">
          <div className="flex h-16 gap-2 px-2">
            <Link
              href={`/story/${state.story.id}`}
              className="flex grow flex-col justify-center truncate"
            >
              <p className="truncate font-bold">{languageElement?.title}</p>
              <p className="truncate">{languageElement?.subtitle}</p>
            </Link>
            <button
              aria-label={LANG.closePlayer}
              onClick={() =>
                setState((prev) => ({ ...prev, story: undefined }))
              }
            >
              <X size={32} />
            </button>
          </div>

          <div className="flex h-12 items-center justify-between gap-2 p-2 pb-3">
            <div className="flex-1 text-left" aria-hidden={true}>
              {formatTime(currentTime)}
            </div>

            <div className="flex-1">
              {!loading && (
                <button
                  className="text-xl"
                  aria-label={`${playbackRate}x ${LANG.speed}`}
                  onClick={() => {
                    if (playbackRate === 1) setPlaybackRate(1.2);
                    if (playbackRate === 1.2) setPlaybackRate(1.5);
                    if (playbackRate === 1.5) setPlaybackRate(2);
                    if (playbackRate === 2) setPlaybackRate(1);
                  }}
                >
                  <div className="w-16 text-right">{playbackRate}x</div>
                </button>
              )}
            </div>

            <button
              disabled={loading}
              onClick={handlePlayPause}
              aria-label={playing ? LANG.pause : LANG.play}
            >
              {loading && <LoaderCircle size={38} className="animate-spin" />}
              {!loading &&
                (playing ? (
                  <CirclePause size={44} style={{ strokeWidth: 2 }} />
                ) : (
                  <CirclePlay size={44} style={{ strokeWidth: 2 }} />
                ))}
            </button>

            <div className="flex-1" />

            <div className="flex-1 text-right" aria-hidden={true}>
              {formatTime(duration)}
            </div>
          </div>

          <div className="relative w-full">
            <input
              type="range"
              className="absolute inset-0 z-20 h-7 -translate-y-3 appearance-none bg-transparent [&::-moz-range-thumb]:size-3 [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-yellow-300 [&::-webkit-slider-thumb]:size-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:bg-yellow-300"
              min={0}
              onChange={(e) =>
                audioRef.current &&
                (audioRef.current.currentTime = parseFloat(e.target.value))
              }
              step={0.001}
              value={currentTime ?? 0}
              max={duration ?? 0}
            />
          </div>

          <div className="relative h-1">
            <div
              className="bg-accent dark:bg-accent-dark absolute inset-0"
              style={{
                right: `${100 - (currentTime / duration) * 100}%`,
              }}
            />
          </div>

          {state.playlist && (
            <div className="bg-primary/10 dark:bg-primary-dark/10 flex h-11 items-center gap-2">
              <button
                onClick={() => handlePlaylistNext(-1)}
                disabled={
                  loading || state.playlist.indexOf(state.story.id) === 0
                }
                className="flex flex-1 items-center justify-start disabled:text-gray-400"
              >
                <ChevronLeft size={32} /> Vorherige Story
              </button>

              <p className="text-md font-semibold">
                {state.playlist.indexOf(state.story.id) + 1} /{" "}
                {state.playlist.length}
              </p>

              <button
                onClick={() => handlePlaylistNext(+1)}
                disabled={
                  loading ||
                  state.playlist.indexOf(state.story.id) ===
                    state.playlist.length - 1
                }
                className="flex flex-1 items-center justify-end disabled:text-gray-400"
              >
                Nächste Story <ChevronRight size={32} />
              </button>
            </div>
          )}
        </div>
      </div>
    )
  );
}
